/**
 * Chart rules of the panel audit (25 Oct 2026, F9/F10): no number lives only behind a hover. Every bar or
 * point chart picks one point (the last one until Sercan taps, clicks or uses the arrow keys) and says it
 * in a readout row under the chart; the highest and the last value are written on the chart itself; the
 * title states the takeaway. The frame is a slider for keyboards and screen readers, so a chart is one tab
 * stop whose arrow keys walk the days. Data marks never use the accent ("needs you").
 */

import { type KeyboardEvent, type ReactNode, useState } from 'react';

export interface ChartPick {
  /** The picked point; -1 for an empty chart. */
  index: number;
  pick: (index: number) => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
}

/** The last point is picked until a tap, a click or an arrow key picks another; a shrunk series falls back to the last. */
export function useChartPick(count: number): ChartPick {
  const [picked, setPicked] = useState<number | null>(null);
  const index = count === 0 ? -1 : picked === null || picked >= count ? count - 1 : picked;
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (count === 0) return;
    let next = index;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = Math.max(0, index - 1);
    else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = Math.min(count - 1, index + 1);
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = count - 1;
    else return;
    event.preventDefault();
    setPicked(next);
  };
  return { index, pick: setPicked, onKeyDown };
}

/** Pointer handlers for one point's hit area: a tap or click picks it, and so does a mouse passing over. */
export function pickHandlers(pick: ChartPick, index: number) {
  return {
    onClick: () => pick.pick(index),
    onPointerEnter: (event: { pointerType: string }) => {
      if (event.pointerType === 'mouse') pick.pick(index);
    },
  };
}

/**
 * The focusable frame around a chart: a slider over its points. `valueText` is what the readout says, so a
 * screen reader hears the picked point while the arrow keys move.
 */
export function ChartFrame({
  label,
  count,
  pick,
  valueText,
  className,
  children,
}: {
  label: string;
  count: number;
  pick: ChartPick;
  valueText: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-orientation="horizontal"
      aria-valuemin={1}
      aria-valuemax={Math.max(1, count)}
      aria-valuenow={Math.max(1, pick.index + 1)}
      aria-valuetext={valueText}
      onKeyDown={pick.onKeyDown}
      className={className ? `chart-frame ${className}` : 'chart-frame'}
    >
      {children}
    </div>
  );
}

/** The readout row under a chart: the picked point in words. */
export function ChartReadout({ children }: { children: ReactNode }) {
  return (
    <p className="chart-readout" aria-hidden="true">
      {children}
    </p>
  );
}

/** The takeaway line on top of a chart. */
export function ChartTakeaway({ children }: { children: ReactNode }) {
  return <p className="chart-takeaway">{children}</p>;
}

/**
 * The highest point and the last point, the two a chart labels directly (one when they are the same). When the
 * two would touch, only the highest keeps its label: the readout row says the last point until another is picked.
 */
export function labelledPoints(values: readonly number[]): number[] {
  if (values.length === 0) return [];
  const last = values.length - 1;
  let peak = 0;
  values.forEach((value, index) => {
    if (value > (values[peak] ?? 0)) peak = index;
  });
  if (peak === last || (values[peak] ?? 0) === 0) return [last];
  return last - peak < Math.max(2, Math.ceil(values.length * 0.15)) ? [peak] : [peak, last];
}

/**
 * Value labels as HTML over a plot whose SVG is stretched to its box (text inside it would stretch too):
 * placed by percent from the left and from the bottom of the plot.
 */
export function BarValueLabels({
  values,
  max,
  text,
}: {
  values: readonly number[];
  max: number;
  text: (value: number) => string;
}) {
  const count = values.length;
  return (
    <>
      {labelledPoints(values).map((index) => {
        const value = values[index] ?? 0;
        const left = ((index + 0.5) / Math.max(1, count)) * 100;
        const bottom = (value / Math.max(1, max)) * 100;
        const edge =
          index === count - 1 || left > 92
            ? 'chart-value chart-value--end'
            : left < 8
              ? 'chart-value chart-value--start'
              : 'chart-value';
        return (
          <span
            key={index}
            className={edge}
            style={{ left: `${left}%`, bottom: `min(${bottom}%, calc(100% - 16px))` }}
            aria-hidden="true"
          >
            {text(value)}
          </span>
        );
      })}
    </>
  );
}
